import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { ModalService } from '../../../../services/modal.service';
import { Observable } from 'rxjs';
import { share } from 'rxjs/operators';
import { ItemsPerPageComponent } from '../../../../components/itemsPerPage.component';
import { I18nTranslator } from '../../../../i18n/translate';
import { AROrgeResultType } from '../../../../models/agrorendement-orge-lists.model';
import { AROrgeCampaign, AROrgeOrderItem } from '../../../../models/agrorendement-orge.model';
import { BreakOnUnderscorePipe } from '../../../../pipes/breakOnUnderscore.pipe';
import { CampaignYearPipe } from '../../../../pipes/campaignYear.pipe';
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe';
import { ExtractAttributePipe } from '../../../../pipes/extractAttribute.pipe';
import { HectareConverterPipe } from '../../../../pipes/hectareConverter.pipe';
import { ExtractFarmNameFromOrderItemsPipe, ExtractParcelNameFromOrderItemsPipe } from '../../../../pipes/results.pipe';
import { ToLabelPipe } from '../../../../pipes/toLabel.pipe';
import { AROrgeListType, AROrgeListsService } from '../../../../services/agrorendement-orge-lists.service';
import { AROrgeService } from '../../../../services/agrorendement-orge.service';
import { FarmService } from '../../../../services/farm.service';
import { KeycloakService } from '../../../../services/keycloak.service';
import { OrganisationService } from '../../../../services/organisation.service';
import { UserService } from '../../../../services/user.service';
import { Tooltip } from '../../../../utils/Tooltip';
import { TooltipContent } from '../../../../utils/TooltipContent';
import { sortingString, stob, toNull, toNumber } from '../../../../utils/utils';
import { ARResultsComponent } from '../common/results.component';
import { AROrgeUtils } from './orge.utils';
import { TranslatePipe, TranslateDatePipe } from '../../../../pipes/translate.pipe';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { MultiSelectComponent } from '../../../../components/multi-select/multi-select.component';

@Component({
  standalone: true,
  selector: 'ar-orge-results-cmp',
  templateUrl: '../common/results.component.html',
  styleUrls: ['../common/results.component.scss'],
  imports: [
    MultiSelectComponent,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    CommonModule,
    FormsModule,
    Tooltip,
    TooltipContent,
    ItemsPerPageComponent,
    HectareConverterPipe,
    ToLabelPipe,
    CapitalizePipe,
    ExtractAttributePipe,
    CampaignYearPipe,
    BreakOnUnderscorePipe,
    ExtractParcelNameFromOrderItemsPipe,
    ExtractFarmNameFromOrderItemsPipe,
    TranslatePipe,
    TranslateDatePipe,
    PaginationModule,
    TooltipModule,
  ],
  providers: [AROrgeService, FarmService, OrganisationService, UserService],
})
export class AROrgeResultsComponent extends ARResultsComponent<AROrgeOrderItem, AROrgeCampaign, AROrgeResultType> {
  @ViewChild('termPrefixFilterParcel') public termPrefixFilterParcel: ElementRef;
  @ViewChild('termPrefixFilterExploitation') public termPrefixFilterExploitation: ElementRef;
  @ViewChild('termFarmFilter') termFarmFilter: ElementRef;
  @ViewChild('termCampaignFilter') termCampaignFilter: ElementRef;
  @ViewChild('termOrganisationFilter') termOrganisationFilter: ElementRef;
  @ViewChild('termValidationStatusFilter') termValidationStatusFilter: ElementRef;
  @ViewChild('termAvailabilityStatusFilter') termAvailabilityStatusFilter: ElementRef;
  @ViewChild('termMarkedForNotificationStatusFilter') termMarkedForNotificationStatusFilter: ElementRef;
  @ViewChild('termNotificationStatusFilter') termNotificationStatusFilter: ElementRef;
  @ViewChild('termNotificationAdvisorsStatusFilter') termNotificationAdvisorsStatusFilter: ElementRef;
  @ViewChild('termPrefixFilterIdParcel') termPrefixFilterIdParcel: ElementRef;
  @ViewChild('allFilters', { static: true }) allFilters: ElementRef;
  @ViewChild('termAdvisorFilter') termAdvisorFilter: ElementRef;

  @ViewChildren('resultCheckbox') resultCheckboxes: any;
  @ViewChild('checkboxAll') checkboxAll: any;
  isAdmin: boolean;
  constructor(
    protected service: AROrgeService,
    protected listService: AROrgeListsService,
    protected organisationService: OrganisationService,
    protected farmService: FarmService,
    protected i18nTranslator: I18nTranslator,
    protected router: Router,
    protected route: ActivatedRoute,
    protected modal: ModalService,
    protected ref: ChangeDetectorRef,
    protected userService: UserService,
  ) {
    super();
    this.specyString = this.i18nTranslator.translate('ar.orge.section');
    this.specyUrlPart = ['azote', 'orge'];
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
  }

  isCampaignCurrentlyOpen(campaign: AROrgeCampaign): boolean {
    return AROrgeUtils.isCampaignCurrentlyOpen(campaign);
  }

  getResultTypes(): Observable<Map<any, AROrgeResultType[]>> {
    const listTypes = [AROrgeListType.ORGE_RESULT_TYPES];
    const obs1 = this.listService.getListTypes(AROrgeListType, ...listTypes).pipe(share());
    obs1.subscribe((lists) => {
      this.resultTypes = <AROrgeResultType[]>lists.get(AROrgeListType.ORGE_RESULT_TYPES).sort(sortingString('label'));
      const rawResultTypes: any = this.resultTypes;

      this.resultTypes = this.resultTypes.filter(function (item) {
        return item.visible;
      });

      this.resultTypesDict = new Map<string, AROrgeResultType>();
      for (const resultType of rawResultTypes) {
        this.resultTypesDict.set(resultType.code, resultType);
      }
    });
    return <Observable<Map<any, AROrgeResultType[]>>>obs1;
  }

  isGenerateConsole = true;
  navigateToGenerateConsole(): any {
    let notificationAdvisorsStatus: boolean = null;
    if (this.notificationAdvisorsStatusFilter) {
      notificationAdvisorsStatus = stob('' + this.notificationAdvisorsStatusFilter);
    }
    const filterToSave = JSON.stringify({
      prefixFilterParcel: this.prefixFilterParcel,
      prefixFilterExploitation: this.prefixFilterExploitation,
      prefixFilterIdParcel: toNumber(this.prefixFilterIdParcel),
      campaignFilter: toNull(this.campaignFilter),
      organisationFilter: toNull(this.organisationFilter),
      resultTypeFilter: toNull(this.resultTypeFilter),
      validationStatusFilter: stob('' + this.validationStatusFilter),
      availabilityStatusFilter: stob('' + this.availabilityStatusFilter),
      notificationStatusFilter: stob('' + this.notificationStatusFilter),
      notificationAdvisorsStatusFilter: notificationAdvisorsStatus,
      advisorFilter: toNull(this.advisorFilter),
    });
    sessionStorage.setItem(this.specyUrlPart[1] + 'Filter', filterToSave);
    this.router.navigate(['/results', 'azote', 'orge', 'genconsole']);
  }
}
